import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { layers } from '@atlaskit/theme/constants';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useApprovals,
	useApprovalsDispatch,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_ANALYTICS_SOURCE,
	getFailureStatusCode,
} from '@confluence/approvals/entry-points/analytics.ts';
import { LiveDocConversionBlockedModal } from '@confluence/approvals/entry-points/LiveDocConversionBlockedModal.ts';
import { useLiveDocConversionBlockedModal } from '@confluence/approvals/entry-points/useLiveDocConversionBlockedModal.ts';
import { ApprovalActionConfirmationModal } from '@confluence/approvals/entry-points/ApprovalActionConfirmationModal.ts';
import { ChangeStatusAndCancelApprovalModal } from '@confluence/approvals/entry-points/ChangeStatusAndCancelApprovalModal.ts';
import { CancelApprovalModal } from '@confluence/approvals/entry-points/CancelApprovalModal.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	APPROVALS_PANE_ID,
	openApprovalsPaneForContext,
	type ApprovalsPaneEntryPoint,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { APPROVALS_LIVE_DOC_PUBLISH_MODAL_SOURCE } from '@confluence/approvals/entry-points/liveDocApprovalPublishModalSource.ts';
import { usePublishModalMountActions } from '@confluence/publish-modal-state/entry-points/PublishModalMountState.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { useUserProfileData } from '../hooks/useUserProfileData.ts';
import { colorCodeToColorName, tokenToHex } from '../helpers.ts';
import { LiveDocStartApprovalPopup } from '../ContentStateInfoPopup/ApprovalStatusButton/LiveDocStartApprovalPopup.tsx';
import { ContentStateInfoPopup } from '../ContentStateInfoPopup/ContentStateInfoPopup.tsx';
import { OtherModeReviewBlockingPopup } from '../OtherModeReviewBlockingPopup/OtherModeReviewBlockingPopup.tsx';

import { ContentStateSkeleton } from './ContentStateSkeleton.tsx';
import { ContentStateButton } from './ContentStateButton.tsx';
import { ContentStateMenu } from './ContentStateMenu.tsx';
import { ContentStateMenuV2 } from './ContentStateMenuV2/ContentStateMenuV2.tsx';
import { ApprovalsUpsellModal } from './ContentStateMenuV2/ApprovalsUpsellModal.tsx';
import {
	createNewCustomStatesArrayWhenExistingCustomSelected,
	createNewCustomStatesArrayWhenNewCustomAdded,
	OPTIMISTIC_CUSTOM_STATE_ID_PREFIX,
	reconcileCustomStatesAfterCreate,
	isDuplicate,
} from './helpers.ts';
import type { ContentState, EditorContentStateProps } from './types.ts';
import { i18n } from './i18n.ts';
import EditorContentStateQuery from './__generated__/EditorContentStateQuery.graphql.ts';
import type {
	EditorContentStateComponentSetContentStateMutation as EditorContentStateComponentSetContentStateMutation$data,
	EditorContentStateComponentSetContentStateMutationVariables as EditorContentStateComponentSetContentStateMutation$variables,
} from './__types__/EditorContentStateComponentSetContentStateMutation.ts';
import type {
	EditorContentStateComponentRemoveContentStateMutation as EditorContentStateComponentRemoveContentStateMutation$data,
	EditorContentStateComponentRemoveContentStateMutationVariables as EditorContentStateComponentRemoveContentStateMutation$variables,
} from './__types__/EditorContentStateComponentRemoveContentStateMutation.ts';
import type {
	EditorContentStateComponentSetContentStateAndPublishMutation$data,
	EditorContentStateComponentSetContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentSetContentStateAndPublishMutation.graphql.ts';
import type {
	EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
	EditorContentStateComponentRemoveContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentRemoveContentStateAndPublishMutation.graphql.ts';
import type {
	EditorContentStateComponentDisableCustomContentStateMutation$data,
	EditorContentStateComponentDisableCustomContentStateMutation$variables,
} from './__generated__/EditorContentStateComponentDisableCustomContentStateMutation.graphql.ts';

/**
 * A status change the user selected while an end-state approval (APPROVED / DENIED)
 * is active. Stashed so it can be applied only after the user confirms the
 * "Change status?" friction modal. PENDING approvals are handled separately via
 * the cancel-approval flow and never produce a value here.
 *
 * The `workflow` kind is for selecting the review-eligible ("In Review") status again,
 * which on confirm cancels the old approval and starts a fresh approval request.
 */
type PendingStatusChange =
	| { kind: 'existing'; status: ContentState }
	| { kind: 'custom'; name: string; colorId: string }
	| { kind: 'remove' }
	| { kind: 'workflow'; status: WorkflowStatus };

export const EditorContentStateComponentImpl: FC<EditorContentStateProps> = ({
	contentId,
	contentType,
	contentStateState,
	userOffline,
	flags,
	forceContentStatePlacementToTop,
	onUpdateSuccess,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
	isViewMode,
	canEdit = true,
}) => {
	const contentStateButtonRef = useRef(null);
	const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState<boolean>(false);
	const [isShowingInfoPopup, setIsShowingInfoPopup] = useState<boolean>(false);
	const [isShowingOtherModeBlockingPopup, setIsShowingOtherModeBlockingPopup] =
		useState<boolean>(false);
	const [isShowingLiveDocStartApprovalPopup, setIsShowingLiveDocStartApprovalPopup] =
		useState<boolean>(false);
	const [isCreatingContentState, setIsCreatingContentState] = useState<boolean>(false);
	const [selectedColor, setSelectedColor] = useState<string | null>(null);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false);
	const [isHoveringRemove, setIsHoveringRemove] = useState<boolean>(false);
	const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
	const [isApprovalsUpsellModalOpen, setIsApprovalsUpsellModalOpen] = useState(false);
	const approvalsPaneEntryPoint: ApprovalsPaneEntryPoint = isDetailsPanel
		? 'detailsPanel'
		: 'byline';
	const liveDocConversionApprovalsPaneEntryPoint: ApprovalsPaneEntryPoint = isDetailsPanel
		? 'detailsPanelLiveDocConversion'
		: 'bylineLiveDocConversion';

	const originalContentState = useRef(contentStateState.state.contentState);

	// Track if we're creating a NEW custom status (V1) vs selecting existing or V2 optimistic
	// Used to determine if onCompleted should update customStatesArray
	const isCreatingNewCustomStatusRef = useRef<boolean>(false);

	// Snapshot of the custom statuses list taken right before an optimistic delete,
	// so the disable mutation's failure handler can restore it.
	const customStatesBeforeDeleteRef = useRef<(ContentState | null)[]>([]);

	// Snapshot of the applied page status taken right before an optimistic delete, so the
	// failure handler can restore it when the deleted status was the applied one.
	const contentStateBeforeDeleteRef = useRef<ContentState | null>(null);

	// True when the optimistic delete also cleared the applied page status.
	const didClearAppliedStatusOnDeleteRef = useRef<boolean>(false);

	// Guards against concurrent deletes. The snapshot ref above only holds a single
	// in-flight operation, so we ignore additional delete clicks until the current
	// disable mutation resolves (success or failure).
	const isDeletingCustomStatusRef = useRef<boolean>(false);

	// Restore the optimistically-removed option after a failed custom-status delete
	// (network error or a 200 with success: false / errors), and surface an error.
	const restoreAfterFailedDelete = useCallback(() => {
		isDeletingCustomStatusRef.current = false;
		// Restore the rollback list before releasing the in-flight guard, so a refetch
		// can't overwrite it with stale server data.
		void contentStateState.setCustomStatesArray(customStatesBeforeDeleteRef.current);
		if (didClearAppliedStatusOnDeleteRef.current) {
			void contentStateState.setContentState(contentStateBeforeDeleteRef.current);
			didClearAppliedStatusOnDeleteRef.current = false;
			// The applied status is restored, so the keep-empty-trigger guard no longer applies.
			contentStateState.setKeepEmptyTriggerForContentId(null);
		}
		contentStateState.setIsOptimisticOperationPending(false);
		void contentStateState.setIsUpdating(false);
		void flags.showErrorFlag({
			id: 'content-state-delete-error-flag',
			title: <FormattedMessage {...i18n.errorFlagTitle} />,
			description: <FormattedMessage {...i18n.errorFlagBody} />,
		});
	}, [contentStateState, flags]);

	const setIsContentStateMenuOpenWithListener = useCallback(
		(newValue: boolean) => {
			onContentStateMenuToggle?.(newValue);
			setIsContentStateMenuOpen(newValue);
		},
		[onContentStateMenuToggle],
	);

	const isLivePage = useIsCurrentPageLive();
	const { cloudId } = useSessionData();
	const isSiteAdmin = useIsAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Suppress the loading skeleton during SSR/initial-hydration to avoid a CLIENT_MISMATCH
	// hydration error: the server renders no skeleton (multiple sources of truth combine to
	// `false`), but the client's first render of the real ApprovalsProvider has
	// `approvalRequestLoading=true` (initial useState before useEffect runs the fetch).
	// Rendering the skeleton only after hydration completes keeps the first client render
	// identical to the server render. See PR #349185 / CCIMPORTER-1635 follow-up.
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const contentState = contentStateState.state.contentState;
	const contentStateLastUpdated = contentStateState.state.contentStateLastUpdated;

	// Approvals context hooks (safe to call unconditionally — returns defaults when provider absent)
	const {
		availableWorkflowStatuses,
		approvalRequest,
		approvalRequestLoading,
		otherModeReviewStatus,
		isContentApprovalsEnabled,
		disallowFirstPublish,
	} = useApprovals();
	const {
		createWorkflowApplication,
		cancelWorkflowApplication,
		clearApprovalRequestOptimistically,
		requestLiveDocApprovalConversion,
	} = useApprovalsDispatch();
	const { openPublishModal } = usePublishModalMountActions();
	const {
		handleCloseLiveDocConversionBlockedModal,
		handleLiveDocConversionBlockedByApproval,
		isLiveDocConversionBlockedModalOpen,
		shouldBlockLiveDocConversion,
	} = useLiveDocConversionBlockedModal({
		contentId,
		contentType,
		createAnalyticsEvent,
		disallowFirstPublish,
		isLivePage,
	});

	const activeWorkflowStatus = useMemo((): WorkflowStatus | null => {
		const activeStatusId = approvalRequest?.application?.workflowStatusId;
		if (!activeStatusId) return null;
		return availableWorkflowStatuses.find((s) => s.id === activeStatusId) ?? null;
	}, [approvalRequest, availableWorkflowStatuses]);

	// Memoize the review-eligible statuses — empty when space-level approvals toggle is off
	const reviewEligibleStatuses = useMemo(
		() =>
			isContentApprovalsEnabled ? availableWorkflowStatuses.filter((s) => s.allowsReviews) : [],
		[availableWorkflowStatuses, isContentApprovalsEnabled],
	);
	const hasSiteApprovalWorkflowStatus = useMemo(
		() => availableWorkflowStatuses.some((status) => status.allowsReviews),
		[availableWorkflowStatuses],
	);
	const shouldShowApprovalsUpsell =
		isSiteAdmin &&
		!approvalRequestLoading &&
		!isContentApprovalsEnabled &&
		!hasSiteApprovalWorkflowStatus;

	/**
	 * True when there is an active approval review in an *end state* (APPROVED / DENIED).
	 * These are the only reviews that surface the "Change status" action in the info popup;
	 * PENDING reviews surface "Cancel approval" and are handled by the separate cancel flow.
	 */
	const hasEndStateApproval =
		!!activeWorkflowStatus &&
		!!approvalRequest?.review &&
		approvalRequest.review.reviewStatus !== 'CANCELED' &&
		approvalRequest.review.reviewStatus !== 'PENDING';

	const respondedApproverData = useMemo(() => {
		if (!approvalRequest?.review?.approvers) return [];
		return approvalRequest.review.approvers.reduce<
			Array<{ name: string; src?: string; email?: string; userId?: string }>
		>((acc, a) => {
			if (a.status !== null && a.status !== 'PENDING_REVIEW') {
				acc.push({
					name: a.name,
					src: a.avatarUrl,
					email: a.email,
					...(a.principalType === 'USER' && a.principalId ? { userId: a.principalId } : {}),
				});
			}
			return acc;
		}, []);
	}, [approvalRequest]);

	// Fetch user data for content state (skip when workflow status is active — that data comes from ApprovalsContext)
	const shouldFetchUserData =
		!activeWorkflowStatus &&
		contentState?.lastModifierAaid &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);
	const { updatedBy: contentStateUpdatedBy, loading: contentStateUserLoading } = useUserProfileData(
		shouldFetchUserData ? contentState.lastModifierAaid : null,
	);

	// Resolve update metadata: workflow status data from ApprovalsContext,
	// content state data from the query above
	const updatedBy = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedBy
		: contentStateUpdatedBy;
	const updatedAt = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedDate || undefined
		: (contentStateLastUpdated ?? undefined);
	const userDataLoading = activeWorkflowStatus ? false : contentStateUserLoading;

	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(() => {
		return (
			!localStorage?.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		);
	});
	const [removeContentStateMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateMutation$data,
		EditorContentStateComponentRemoveContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateMutation($contentId: ID!) {
				removeContentState(contentId: $contentId)
			}
		`,
		{
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
			onCompleted: () => {
				// During a custom-status delete this mutation is chained before the disable
				// mutation, which signals final completion; skip here to avoid firing twice.
				if (isDeletingCustomStatusRef.current) {
					return;
				}
				onUpdateSuccess?.();
			},
		},
	);
	const [setContentStateMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateMutation$data,
		EditorContentStateComponentSetContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					contentStateState.setIsOptimisticOperationPending(false);
				}
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				// Only update customStatesArray when creating a NEW custom status (V1 flow)
				// V2 handles this optimistically before the mutation
				if (data.setContentState && isCreatingNewCustomStatusRef.current) {
					// The approvals-beta flow adds an optimistic placeholder that must be reconciled
					// away; the legacy (gate-off) flow keeps its original dedupe behaviour untouched.
					const newCustomStatesArray = expValEquals('confluence_approvals_eap', 'isEnabled', true)
						? reconcileCustomStatesAfterCreate(
								contentStateState.state.customStatesArray,
								data.setContentState,
							)
						: createNewCustomStatesArrayWhenNewCustomAdded(
								contentStateState.state.customStatesArray,
								data.setContentState,
							);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					// Swap the temp-id optimistic status for the server one so the menu keeps it selected.
					if (
						contentStateState.state.contentState?.id?.startsWith(
							OPTIMISTIC_CUSTOM_STATE_ID_PREFIX,
						) &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true)
					) {
						void contentStateState.setContentState(data.setContentState);
					}
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentState) {
					originalContentState.current = data.setContentState;
				}
				// Authoritative state applied; release the guard (gated to match the set).
				if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					contentStateState.setIsOptimisticOperationPending(false);
				}
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const [disableCustomContentStateMutation] = useMutationInterop<
		EditorContentStateComponentDisableCustomContentStateMutation$data,
		EditorContentStateComponentDisableCustomContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentDisableCustomContentStateMutation(
				$cloudId: ID!
				$contentStateId: ID!
			) {
				confluence_disableCustomContentState(cloudId: $cloudId, contentStateId: $contentStateId) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			onError: () => {
				restoreAfterFailedDelete();
			},
			onCompleted: (data) => {
				// A 200 response can still represent a server-side failure: inspect the
				// payload's success/errors fields and treat a failure like onError so we
				// don't leave the UI in an optimistically-removed state.
				const payload = data?.confluence_disableCustomContentState;
				const failed = !payload?.success || (payload?.errors?.length ?? 0) > 0;
				if (failed) {
					restoreAfterFailedDelete();
					return;
				}
				// Disable persisted; clear the delete flags and release the guard.
				isDeletingCustomStatusRef.current = false;
				didClearAppliedStatusOnDeleteRef.current = false;
				contentStateState.setIsOptimisticOperationPending(false);
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			// Refetch the source query so the Apollo cache is updated and every
			// consumer of the available content states (not just this menu's local
			// optimistic copy) reflects the deletion.
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const [setContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateAndPublishMutation$data,
		EditorContentStateComponentSetContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateAndPublishMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
				$suppressDraftPropertyUpdate: Boolean
				$cloudId: ID!
			) {
				setContentStateAndPublish(
					cloudId: $cloudId
					contentId: $contentId
					contentState: $contentStateInput
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					contentStateState.setIsOptimisticOperationPending(false);
				}
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'setContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				if (data.setContentStateAndPublish && isCreatingNewCustomStatusRef.current) {
					// The approvals-beta flow adds an optimistic placeholder that must be reconciled
					// away; the legacy (gate-off) flow keeps its original dedupe behaviour untouched.
					const newCustomStatesArray = expValEquals('confluence_approvals_eap', 'isEnabled', true)
						? reconcileCustomStatesAfterCreate(
								contentStateState.state.customStatesArray,
								data.setContentStateAndPublish,
							)
						: createNewCustomStatesArrayWhenNewCustomAdded(
								contentStateState.state.customStatesArray,
								data.setContentStateAndPublish,
							);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					// Swap the temp-id optimistic status for the server one so the menu keeps it selected.
					if (
						contentStateState.state.contentState?.id?.startsWith(
							OPTIMISTIC_CUSTOM_STATE_ID_PREFIX,
						) &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true)
					) {
						void contentStateState.setContentState(data.setContentStateAndPublish);
					}
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentStateAndPublish) {
					originalContentState.current = data.setContentStateAndPublish;
				}
				// Authoritative state applied; release the guard (gated to match the set).
				if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					contentStateState.setIsOptimisticOperationPending(false);
				}
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
		},
	);

	const [removeContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
		EditorContentStateComponentRemoveContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateAndPublishMutation(
				$contentId: ID!
				$doPublish: Boolean
				$suppressDraftPropertyUpdate: Boolean
			) {
				removeContentState(
					contentId: $contentId
					doPublish: $doPublish
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				)
			}
		`,
		{
			onError: (error) => {
				// During a custom-status delete, restoreAfterFailedDelete (the `.catch()`) is the
				// single source of error recovery; this is an expected, fully-recovered error, so mark
				// it handled to silence monitoring and bail out to avoid a duplicate flag.
				if (isDeletingCustomStatusRef.current) {
					markErrorAsHandled(error);
					return;
				}
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-remove-error-flag',
					title: <FormattedMessage {...i18n.removeErrorFlagTitle} />,
					description: <FormattedMessage {...i18n.removeErrorFlagBody} />,
				});
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'removeContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
			onCompleted: () => {
				// During a custom-status delete this mutation is chained before the disable
				// mutation, which signals final completion; skip here to avoid firing twice.
				if (isDeletingCustomStatusRef.current) {
					return;
				}
				onUpdateSuccess?.();
			},
		},
	);

	const activeRemoveMutation =
		isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)
			? removeContentStateAndPublishMutation
			: removeContentStateMutation;

	const rightSidebarContext = useRightSidebarContext();
	const isApprovalsPaneOpen = rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID;
	const wasApprovalsPaneOpenRef = useRef(isApprovalsPaneOpen);

	// Close the content state info popup only at the moment the approvals pane opens,
	// not continuously while it is open — users should still be able to reopen the popup.
	// TODO: revisit this logic to see if it can be simplified.
	useEffect(() => {
		if (isApprovalsPaneOpen && !wasApprovalsPaneOpenRef.current && isShowingInfoPopup) {
			setIsShowingInfoPopup(false);
		}
		wasApprovalsPaneOpenRef.current = isApprovalsPaneOpen;
	}, [isApprovalsPaneOpen, isShowingInfoPopup]);

	// Reset color picker when menu closes
	useEffect(() => {
		if (!isContentStateMenuOpen) {
			setSelectedColor(null);
			setIsColorPickerOpen(false);
		}
	}, [isContentStateMenuOpen]);

	const applyExistingStatus = useCallback(
		(selectedStatus: ContentState) => {
			if (isViewMode && !cloudId) {
				return;
			}
			isCreatingNewCustomStatusRef.current = false;
			void contentStateState.setIsUpdating(true);
			if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				void setContentStateAndPublishMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
						suppressDraftPropertyUpdate: true,
						cloudId,
					},
				});
			} else {
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
					},
				});
			}
			void contentStateState.setContentState(selectedStatus);

			const customStatesArray = contentStateState.state.customStatesArray;
			const customIndex = customStatesArray.findIndex(
				(s) => s !== null && s.id === selectedStatus.id,
			);
			if (customIndex !== -1) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					customStatesArray,
					selectedStatus,
					customIndex,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			const isCustom = customIndex !== -1;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: isCustom ? 'custom' : 'suggested',
						color: colorCodeToColorName(selectedStatus.color?.toUpperCase() || ''),
						restrictionLevel: selectedStatus.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const applyCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			if (isViewMode && !cloudId) {
				return;
			}
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: colorId.toLowerCase(),
					name: statusName,
				})
			) {
				const newContentState = { color: colorId, name: statusName };
				void contentStateState.setIsUpdating(true);
				// Guard the optimistic create across its refetch.
				if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					contentStateState.setIsOptimisticOperationPending(true);
				}
				isCreatingNewCustomStatusRef.current = true;

				const optimisticCustomState = {
					...newContentState,
					id: `${OPTIMISTIC_CUSTOM_STATE_ID_PREFIX}${Date.now()}`,
				};
				const updatedCustomStatesArray = [
					optimisticCustomState,
					...contentStateState.state.customStatesArray,
				];
				void contentStateState.setCustomStatesArray(updatedCustomStatesArray);

				if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					void setContentStateAndPublishMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
							suppressDraftPropertyUpdate: true,
							cloudId,
						},
					});
				} else {
					void setContentStateMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
						},
					});
				}
				// Apply the optimistic status. Behind the gate, carry the temp id so the V2 menu can
				// match it; onCompleted swaps in the server status. Non-gated keeps the id-less value.
				void contentStateState.setContentState(
					expValEquals('confluence_approvals_eap', 'isEnabled', true)
						? optimisticCustomState
						: newContentState,
				);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(colorId),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const handleSelectExistingStatusV2 = useCallback(
		(selectedStatus: ContentState) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			// End-state approval (APPROVED / DENIED): defer until the user confirms the
			// "Change status?" friction modal, then apply this selection in handleConfirmChangeStatus.
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'existing', status: selectedStatus });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyExistingStatus(selectedStatus);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			hasEndStateApproval,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyExistingStatus,
		],
	);

	const handleSaveCustomStatusV2 = useCallback(
		(statusName: string, colorId: string) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			// End-state approval (APPROVED / DENIED): defer until the user confirms the
			// "Change status?" friction modal, then apply this selection in handleConfirmChangeStatus.
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'custom', name: statusName, colorId });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyCustomStatus(statusName, colorId);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			hasEndStateApproval,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyCustomStatus,
		],
	);

	const handleDeleteCustomStatusV2 = useCallback(
		(status: ContentState) => {
			if (!expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				return;
			}

			if (!status.id || !cloudId) {
				return;
			}
			// Capture the narrowed id so the closures below keep the non-null type.
			const contentStateId = status.id;

			// Only one delete may be in flight at a time; the snapshot refs hold a single
			// operation, so concurrent deletes would corrupt rollback on failure.
			if (isDeletingCustomStatusRef.current) {
				return;
			}
			isDeletingCustomStatusRef.current = true;

			const currentCustomStatesArray = contentStateState.state.customStatesArray;
			// Snapshot the option list for rollback on failure.
			customStatesBeforeDeleteRef.current = currentCustomStatesArray;

			// Mark in-flight before the mutation so the refetch can't clobber the optimistic update.
			void contentStateState.setIsUpdating(true);
			contentStateState.setIsOptimisticOperationPending(true);

			const updatedCustomStatesArray = currentCustomStatesArray.filter(
				(customStatus) => customStatus?.id !== status.id,
			);
			void contentStateState.setCustomStatesArray(updatedCustomStatesArray);

			const fireDisableMutation = () =>
				disableCustomContentStateMutation({
					variables: {
						cloudId,
						contentStateId,
					},
				});

			// If the deleted status is applied, clear + persist its removal first, then chain the
			// disable mutation (one settled refetch, no byline flicker). Otherwise just disable.
			const appliedContentState = contentStateState.state.contentState;
			const deletedStatusIsApplied = !!appliedContentState && appliedContentState.id === status.id;
			didClearAppliedStatusOnDeleteRef.current = deletedStatusIsApplied;
			// In view mode, keep the empty add-status trigger visible after deleting the applied
			// status since the menu can't stay open. Scoped to this contentId so it resets on
			// navigation; a fresh container resets it on refresh.
			if (deletedStatusIsApplied && isViewMode) {
				contentStateState.setKeepEmptyTriggerForContentId(contentId);
			}
			if (deletedStatusIsApplied) {
				contentStateBeforeDeleteRef.current = appliedContentState;
				void contentStateState.setContentState(null);
				void activeRemoveMutation({
					variables: {
						contentId,
						...(isViewMode &&
							expValEquals('confluence_approvals_eap', 'isEnabled', true) && {
								doPublish: true,
								suppressDraftPropertyUpdate: true,
							}),
					},
				})
					.then(() => fireDisableMutation())
					// Roll the whole delete back on failure. The disable mutation's own onError
					// also calls restoreAfterFailedDelete (which clears this guard), so bail out if
					// it already ran to avoid a duplicate error flag.
					.catch(() => {
						if (isDeletingCustomStatusRef.current) {
							restoreAfterFailedDelete();
						}
					});
			} else {
				void fireDisableMutation();
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateDeleted',
					source: 'editorContentState',
					attributes: {
						type: 'custom',
						color: colorCodeToColorName(status.color ?? ''),
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			activeRemoveMutation,
			cloudId,
			contentId,
			contentStateState,
			createAnalyticsEvent,
			disableCustomContentStateMutation,
			isLivePage,
			isViewMode,
			restoreAfterFailedDelete,
		],
	);

	const applyRemove = useCallback(() => {
		isCreatingNewCustomStatusRef.current = false;
		if (contentStateState.state.contentState) {
			void contentStateState.setIsUpdating(true);
			void activeRemoveMutation({
				variables: {
					contentId,
					...(isViewMode &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true) && {
							doPublish: true,
							suppressDraftPropertyUpdate: true,
						}),
				},
			});
			void contentStateState.setContentState(null);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateRemoved',
					source: 'editorContentState',
					attributes: {
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		}
	}, [
		contentStateState,
		activeRemoveMutation,
		contentId,
		createAnalyticsEvent,
		isLivePage,
		isViewMode,
	]);

	const handleRemoveV2 = useCallback(() => {
		if (
			activeWorkflowStatus &&
			approvalRequest?.review &&
			approvalRequest.review.reviewStatus === 'PENDING'
		) {
			setShowChangeStatusModal(true);
			return;
		}

		// End-state approval (APPROVED / DENIED): defer until the user confirms the
		// "Change status?" friction modal, then apply the removal in handleConfirmChangeStatus.
		if (hasEndStateApproval) {
			setPendingStatusChange({ kind: 'remove' });
			setShowChangeStatusConfirmModal(true);
			return;
		}

		if (activeWorkflowStatus) {
			clearApprovalRequestOptimistically();
			void cancelWorkflowApplication();
		}

		setIsContentStateMenuOpenWithListener(false);
		applyRemove();
	}, [
		activeWorkflowStatus,
		approvalRequest,
		hasEndStateApproval,
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
		applyRemove,
	]);

	// Performs the actual "start approval" work for a review-eligible status. This is the
	// guard-free core of handleSelectWorkflowStatus so it can be reused after the user confirms
	// the "Change status?" friction modal (where the end-state approval guard must not re-trigger).
	const startWorkflowStatus = useCallback(
		async (selectedStatus: WorkflowStatus) => {
			const previousContentState = contentStateState.state.contentState;
			const prevStateId = activeWorkflowStatus?.id || contentState?.id || null;
			const analyticsAttributes = {
				contentId,
				contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
				prevStateId,
				newStateId: selectedStatus.id,
				isNewStatusWorkflow: true,
			};

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'inReviewStatus',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: analyticsAttributes,
				},
			}).fire();

			/**
			 * Live-doc → convert-to-page popup interception.
			 *
			 * Approvals can't run on live docs, so when a review-eligible status is selected
			 * here we close the status menu and show the convert-to-page intermediary popup.
			 * The popup's CTA then dispatches the intent onto ApprovalsContext, which causes
			 * PublishModalMountPoint to auto-open the publish modal in convert-to-page mode.
			 *
			 */
			if (
				selectedStatus.allowsReviews &&
				isLivePage &&
				expValEquals('confluence_approvals_eap', 'isEnabled', true)
			) {
				setIsContentStateMenuOpenWithListener(false);
				setIsShowingLiveDocStartApprovalPopup(true);
				return;
			}

			if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				// New flow: no optimistic In-Review status, no createWorkflowApplication call.
				// The combined mutation is called later from StartApprovalPane.
				setIsContentStateMenuOpenWithListener(false);
				if (otherModeReviewStatus === 'PENDING') {
					setIsShowingOtherModeBlockingPopup(true);
				} else {
					openApprovalsPaneForContext({
						contentId,
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						entryPoint: approvalsPaneEntryPoint,
						rightSidebarContext,
					});
				}
				return;
			}

			void contentStateState.setIsUpdating(true);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);

			try {
				const result = await createWorkflowApplication(selectedStatus);

				if (otherModeReviewStatus === 'PENDING') {
					// Other mode already has an active pending review — show the info popup
					// (which renders the otherModeReviewWarning SectionMessage) instead of
					// opening the ApprovalsPane / StartApprovalPane.
					setIsShowingInfoPopup(true);
				} else {
					openApprovalsPaneForContext({
						contentId,
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						entryPoint: approvalsPaneEntryPoint,
						rightSidebarContext,
					});
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'created',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: { ...analyticsAttributes, applicationId: result.applicationId },
					},
				}).fire();
				void contentStateState.setIsUpdating(false);
			} catch (error) {
				void contentStateState.setIsUpdating(false);
				void contentStateState.setContentState(previousContentState);
				void flags.showErrorFlag({
					id: 'workflow-status-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: {
							...analyticsAttributes,
							failureStatusCode: getFailureStatusCode(error),
						},
					},
				}).fire();
			}
		},
		[
			otherModeReviewStatus,
			createWorkflowApplication,
			contentStateState,
			contentState,
			contentId,
			isLivePage,
			isViewMode,
			activeWorkflowStatus,
			createAnalyticsEvent,
			flags,
			setIsContentStateMenuOpenWithListener,
			setIsShowingLiveDocStartApprovalPopup,
			setIsShowingOtherModeBlockingPopup,
			rightSidebarContext,
			approvalsPaneEntryPoint,
		],
	);

	const handleSelectWorkflowStatus = useCallback(
		(selectedStatus: WorkflowStatus) => {
			// End-state approval (APPROVED / DENIED): selecting "In Review" again must first
			// confirm via the "Change status?" friction modal. On confirm we cancel the old
			// approval and start a fresh approval request (handleConfirmChangeStatus).
			if (hasEndStateApproval) {
				setPendingStatusChange({ kind: 'workflow', status: selectedStatus });
				setShowChangeStatusConfirmModal(true);
				return;
			}

			void startWorkflowStatus(selectedStatus);
		},
		[hasEndStateApproval, startWorkflowStatus],
	);

	const handleSelectApprovalsUpsell = useCallback(() => {
		setIsApprovalsUpsellModalOpen(true);
	}, []);

	const handleCloseApprovalsUpsellModal = useCallback(() => {
		setIsApprovalsUpsellModalOpen(false);
	}, []);

	const handleColorPickerToggle = useCallback(() => {
		setIsColorPickerOpen((prev) => !prev);
	}, []);

	const handleColorPickerClose = useCallback(() => {
		setIsColorPickerOpen(false);
	}, []);

	const handleSelectColor = useCallback((newColor: string) => {
		setSelectedColor(newColor);
		setIsColorPickerOpen(false);
	}, []);

	const handleFocusTextField = useCallback(() => {
		setIsCreatingContentState(true);
	}, []);

	const handleMouseEnterRemove = useCallback(() => {
		setIsHoveringRemove(true);
	}, []);

	const handleMouseLeaveRemove = useCallback(() => {
		setIsHoveringRemove(false);
	}, []);

	const handleContentStatePopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsCreatingContentState(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleSubmit = useCallback(
		(name: string) => {
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: selectedColor?.toLowerCase() ?? null,
					name,
				})
			) {
				const newContentState = { color: selectedColor, name };
				void contentStateState.setIsUpdating(true);
				// Mark that we're creating a new custom status so onCompleted updates the array
				isCreatingNewCustomStatusRef.current = true;
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							...newContentState,
							color: tokenToHex(selectedColor),
						},
					},
				});
				void contentStateState.setContentState(newContentState);
			}
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(selectedColor ?? ''),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
			setIsCreatingContentState(false);
			setIsContentStateMenuOpenWithListener(false);
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			selectedColor,
			setContentStateMutation,
			setIsContentStateMenuOpenWithListener,
			shouldSetAutomationFlag,
		],
	);

	const handleCancel = useCallback(() => {
		setIsCreatingContentState(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateCancel',
				source: 'editorContentState',
				attributes: {
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [contentId, createAnalyticsEvent, isLivePage, isViewMode]);

	const handleRemove = useCallback(() => {
		// check if state exists before mutating since the trash button still shows if the feature is disabled
		// (page might not have a content state)
		if (contentStateState.state.contentState) {
			void removeContentStateMutation({
				variables: {
					contentId,
				},
			});
			setIsCreatingContentState(false);
			setIsHoveringRemove(false);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateRemoved',
				source: 'editorContentState',
				attributes: {
					uiLocation: 'pageTitleSection',
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [
		contentId,
		contentStateState,
		createAnalyticsEvent,
		removeContentStateMutation,
		setIsCreatingContentState,
		setIsContentStateMenuOpenWithListener,
		isLivePage,
		isViewMode,
	]);

	const handlePopupToggle = useCallback(() => {
		setIsContentStateMenuOpenWithListener(!isContentStateMenuOpen);
	}, [isContentStateMenuOpen, setIsContentStateMenuOpenWithListener]);

	const handlePopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsShowingOtherModeBlockingPopup(false);
		setIsShowingLiveDocStartApprovalPopup(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	/**
	 * Live-doc popup "Convert to page" CTA handler.
	 *
	 * Blocks conversion before opening the publish modal when the space requires approval before
	 * first publish. Otherwise, records the approval-conversion intent so PublishModalContext opens
	 * in convert-to-page mode and the converted page can auto-open the approvals pane. When EAP is
	 * enabled, this flow opens the publish modal directly after recording the conversion intent.
	 *
	 */
	const handleConvertLiveDocToPage = useCallback(() => {
		if (shouldBlockLiveDocConversion) {
			handlePopupClose();
			handleLiveDocConversionBlockedByApproval();
			return;
		}

		requestLiveDocApprovalConversion(contentId, liveDocConversionApprovalsPaneEntryPoint);
		handlePopupClose();
		if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			openPublishModal(APPROVALS_LIVE_DOC_PUBLISH_MODAL_SOURCE);
		}
	}, [
		contentId,
		handleLiveDocConversionBlockedByApproval,
		handlePopupClose,
		openPublishModal,
		liveDocConversionApprovalsPaneEntryPoint,
		requestLiveDocApprovalConversion,
		shouldBlockLiveDocConversion,
	]);

	const handleBackFromLiveDocStartApprovalPopup = useCallback(() => {
		setIsShowingLiveDocStartApprovalPopup(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const [showChangeStatusConfirmModal, setShowChangeStatusConfirmModal] = useState(false);

	// A status the user picked while an end-state approval is active, pending their
	// confirmation in the "Change status?" friction modal. Applied on confirm, cleared on dismiss.
	const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange | null>(null);

	const handleChangeStatus = useCallback(() => {
		setIsShowingInfoPopup(false);
		// Defer the friction confirmation: just open the status menu. The "Change status?"
		// confirmation modal is now shown only once the user actually selects a different
		// (non-approval) status — see handleSelectExistingStatusV2 / handleSaveCustomStatusV2 /
		// handleRemoveV2.
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleConfirmChangeStatus = useCallback(() => {
		setShowChangeStatusConfirmModal(false);

		const pending = pendingStatusChange;
		setPendingStatusChange(null);

		if (!pending) {
			// Defensive: no deferred selection — just reopen the menu without cancelling the
			// existing approval (cancelling here would silently discard an active approval for
			// no reason if `pending` is ever null due to a stale closure or batched update).
			setIsContentStateMenuOpenWithListener(true);
			return;
		}

		// Cancel the existing approval so the selected status can be applied / a new approval
		// request can be started. The workflow application is deactivated; prior activity
		// remains available in audit logs (matching the modal copy).
		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();

		setIsContentStateMenuOpenWithListener(false);
		switch (pending.kind) {
			case 'existing':
				applyExistingStatus(pending.status);
				break;
			case 'custom':
				applyCustomStatus(pending.name, pending.colorId);
				break;
			case 'remove':
				applyRemove();
				break;
			case 'workflow':
				// Start a fresh approval request now that the previous approval has been cancelled.
				void startWorkflowStatus(pending.status);
				break;
			default:
				pending satisfies never;
		}
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		pendingStatusChange,
		applyExistingStatus,
		applyCustomStatus,
		applyRemove,
		startWorkflowStatus,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseChangeStatusConfirmModal = useCallback(() => {
		setShowChangeStatusConfirmModal(false);
		setPendingStatusChange(null);
		setIsShowingInfoPopup(true);
	}, []);

	const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);

	const handleClearRequest = useCallback(() => {
		setIsShowingInfoPopup(false);
		setShowCancelApprovalModal(true);
	}, []);

	const handleConfirmClearRequest = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseCancelApprovalModal = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsShowingInfoPopup(true);
	}, []);

	const handleConfirmCancelApproval = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseChangeStatusModal = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const togglePopup = useCallback(() => {
		const shouldShowInfoPopup =
			expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
			((isContentApprovalsEnabled && activeWorkflowStatus !== null) ||
				// Show the read-only info popup for view-only users who have a content state
				(!canEdit && contentState !== null));
		if (shouldShowInfoPopup) {
			if (isShowingInfoPopup) {
				setIsShowingInfoPopup(false);
			} else {
				setIsShowingInfoPopup(true);
			}
		} else if (canEdit) {
			// Only allow opening the content state menu if the user has edit permission
			handlePopupToggle();
		}
		onClick?.();
	}, [
		isContentApprovalsEnabled,
		activeWorkflowStatus,
		isShowingInfoPopup,
		handlePopupToggle,
		onClick,
		canEdit,
		contentState,
	]);

	const renderTrigger = useCallback(
		(triggerProps: any) => (
			<ContentStateButton
				triggerProps={{ ...triggerProps }}
				togglePopup={togglePopup}
				contentState={contentStateState.state.contentState}
				activeWorkflowStatus={activeWorkflowStatus}
				isSelected={isContentStateMenuOpen}
				isWarning={isHoveringRemove}
				userOffline={userOffline}
				contentStateButtonRef={contentStateButtonRef}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				renderPlusButtonEmptyState={renderPlusButtonEmptyState}
				isDetailsPanel={isDetailsPanel}
				canEdit={canEdit}
				useSubtleEmptyStateLabel={
					isViewMode &&
					contentStateState.keepEmptyTriggerForContentId === contentId &&
					expValEquals('confluence_approvals_eap', 'isEnabled', true)
				}
			/>
		),
		[
			togglePopup,
			contentStateState.state.contentState,
			contentStateState.keepEmptyTriggerForContentId,
			contentId,
			activeWorkflowStatus,
			isContentStateMenuOpen,
			isHoveringRemove,
			userOffline,
			floatingToolbarButtonStyle,
			renderPlusButtonEmptyState,
			isDetailsPanel,
			canEdit,
			isViewMode,
		],
	);

	if (
		!renderServerPlaceholder &&
		isContentApprovalsEnabled &&
		approvalRequestLoading &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		return <ContentStateSkeleton />;
	}

	return (
		<>
			<Popup
				testId="content-state-popup"
				isOpen={
					isContentStateMenuOpen ||
					isShowingInfoPopup ||
					isShowingOtherModeBlockingPopup ||
					isShowingLiveDocStartApprovalPopup
				}
				onClose={handlePopupClose}
				placement={forceContentStatePlacementToTop ? 'top-end' : 'bottom-start'}
				zIndex={layers.dialog()}
				trigger={renderTrigger}
				content={({ update }) => {
					if (isShowingOtherModeBlockingPopup) {
						return (
							<OtherModeReviewBlockingPopup
								isViewMode={isViewMode ?? false}
								onChangeStatus={() => {
									setIsShowingOtherModeBlockingPopup(false);
									handleChangeStatus();
								}}
							/>
						);
					}

					if (isShowingLiveDocStartApprovalPopup) {
						return (
							<LiveDocStartApprovalPopup
								onBack={handleBackFromLiveDocStartApprovalPopup}
								onConvertToPage={handleConvertLiveDocToPage}
							/>
						);
					}

					if (isShowingInfoPopup) {
						return (
							<ContentStateInfoPopup
								contentState={contentState}
								activeWorkflowStatus={activeWorkflowStatus}
								updatedAt={updatedAt}
								updatedBy={updatedBy}
								loading={userDataLoading}
								onChangeStatus={handleChangeStatus}
								onClearRequest={handleClearRequest}
								onClose={handlePopupClose}
								onConvertLiveDocToPage={handleConvertLiveDocToPage}
								canEdit={canEdit}
								contentType={contentType}
								isViewMode={isViewMode}
							/>
						);
					}

					if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
						return (
							<ContentStateMenuV2
								contentId={contentId}
								contentStateState={contentStateState}
								contentType={contentType}
								isLivePage={isLivePage}
								defaultStatesArray={contentStateState.state.defaultStatesArray || []}
								customStatesArray={contentStateState.state.customStatesArray || []}
								isContentStateEnabled={contentStateState.state.isContentStateEnabled}
								userOffline={userOffline}
								onSelectExistingStatus={handleSelectExistingStatusV2}
								onSaveCustomStatus={handleSaveCustomStatusV2}
								onDeleteCustomStatus={handleDeleteCustomStatusV2}
								onRemove={handleRemoveV2}
								isContentStateMenuOpen={isContentStateMenuOpen}
								setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
								workflowStatuses={reviewEligibleStatuses}
								onSelectWorkflowStatus={handleSelectWorkflowStatus}
								hasActiveWorkflowStatus={activeWorkflowStatus !== null}
								showApprovalsUpsell={shouldShowApprovalsUpsell}
								onSelectApprovalsUpsell={handleSelectApprovalsUpsell}
							/>
						);
					}

					return (
						<ContentStateMenu
							contentId={contentId}
							contentStateState={contentStateState}
							contentType={contentType}
							isContentStateEnabled={contentStateState.state.isContentStateEnabled}
							isSuggestedEnabled={contentStateState.state.isSuggestedEnabled}
							isCustomEnabled={contentStateState.state.isCustomEnabled}
							customStatesArray={contentStateState.state.customStatesArray}
							defaultStatesArray={contentStateState.state.defaultStatesArray}
							isCreatingContentState={isCreatingContentState}
							selectedColor={selectedColor}
							onSelectColor={handleSelectColor}
							isColorPickerOpen={isColorPickerOpen}
							onColorPickerToggle={handleColorPickerToggle}
							onColorPickerClose={handleColorPickerClose}
							onPopupClose={handleContentStatePopupClose}
							onSubmit={handleSubmit}
							onCancel={handleCancel}
							onRemove={handleRemove}
							onFocusTextField={handleFocusTextField}
							onMouseEnterRemove={handleMouseEnterRemove}
							onMouseLeaveRemove={handleMouseLeaveRemove}
							userOffline={userOffline}
							contentStateButtonRef={contentStateButtonRef}
							onUpdateSuccess={onUpdateSuccess}
							isContentStateMenuOpen={isContentStateMenuOpen}
							setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
							updatePopup={update}
						/>
					);
				}}
			/>
			{showChangeStatusModal && (
				<ChangeStatusAndCancelApprovalModal
					approverData={respondedApproverData}
					onCancelApproval={handleConfirmCancelApproval}
					onClose={handleCloseChangeStatusModal}
				/>
			)}
			{showCancelApprovalModal && (
				<CancelApprovalModal
					approverData={respondedApproverData}
					showProfileCards={
						respondedApproverData.some(({ userId }) => Boolean(userId)) &&
						expValEquals('confluence_approvals_profile_card_updates', 'isEnabled', true)
					}
					onCancelApproval={handleConfirmClearRequest}
					onClose={handleCloseCancelApprovalModal}
				/>
			)}
			{showChangeStatusConfirmModal && (
				<ApprovalActionConfirmationModal
					variant="changeStatus"
					onConfirm={handleConfirmChangeStatus}
					onClose={handleCloseChangeStatusConfirmModal}
				/>
			)}
			{isLivePage && (shouldBlockLiveDocConversion || isLiveDocConversionBlockedModalOpen) && (
				<LiveDocConversionBlockedModal
					isOpen={isLiveDocConversionBlockedModalOpen}
					onClose={handleCloseLiveDocConversionBlockedModal}
				/>
			)}
			{isApprovalsUpsellModalOpen && (
				<ApprovalsUpsellModal onClose={handleCloseApprovalsUpsellModal} />
			)}
		</>
	);
};

export const EditorContentStateComponent: React.ComponentType<
	Omit<EditorContentStateProps, 'flags'>
> = withFlags(EditorContentStateComponentImpl);
